
import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { useNotificationToast } from '@/hooks/useNotificationToast';

interface AttendancePatternProps {
  studentId?: string;
  consecutiveAbsences?: number;
  enableNotifications?: boolean;
  attendanceData?: any[];
}

export function useAttendancePatternNotifications({
  studentId,
  consecutiveAbsences = 3,
  enableNotifications = true,
  attendanceData = []
}: AttendancePatternProps = {}) {
  const [notified, setNotified] = useState(false);
  const notificationToast = useNotificationToast();

  // Function to check attendance patterns - can be called externally
  const checkAttendancePatterns = useCallback((records: any[] = attendanceData) => {
    if (!enableNotifications || !studentId || records.length === 0) {
      return [];
    }

    // Sort attendance data by date
    const sortedAttendance = [...records].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    // Count consecutive absences
    let absenceCount = 0;
    for (const record of sortedAttendance) {
      if (record.status === 'absent') {
        absenceCount++;
        if (absenceCount >= consecutiveAbsences && !notified) {
          // Notify about consecutive absences
          notificationToast.notification(
            'Attendance Pattern Detected',
            `Student has ${absenceCount} consecutive absences. Follow up required.`
          );
          setNotified(true);
          return [{ 
            studentId, 
            absenceCount, 
            pattern: 'consecutive_absences' 
          }];
        }
      } else {
        // Reset counter when a non-absence is found
        break;
      }
    }
    
    return [];
  }, [attendanceData, studentId, consecutiveAbsences, enableNotifications, notified, notificationToast]);

  useEffect(() => {
    // Only run if we have attendance data and notifications are enabled
    if (!enableNotifications || !studentId || attendanceData.length === 0) {
      return;
    }

    checkAttendancePatterns(attendanceData);
  }, [checkAttendancePatterns, attendanceData, studentId, enableNotifications]);

  return {
    resetNotificationStatus: () => setNotified(false),
    checkAttendancePatterns
  };
}
